import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { PrModalConfirmComponent } from '@app/components/pr-modal/pr-modal-confirm/pr-modal-confirm.component';
import { AttributionDao } from '@app/dao/attribution/attribution.dao';
import { PledgeDao } from '@app/dao/pledge/pledge.dao';
import { PledgeRead } from '@app/forms/subscription/definition/interface/Pledge';
import {
  catchError,
  combineLatest,
  finalize,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { InfoOption } from '../../components/info/info.component';
import { CommandEnum } from '../../definition/interface/enum';
import { SubscriptionFacadeService } from '../../services/subscription-facade.service';
import { verifySwisslifeCustomPledgeRules } from './document-verification-utils';
import { DOCUMENT_EDITOR_TOOLBAR_BUTTONS } from './froala-editor/froala-editor.component';

@Component({
  selector: 'document-verification',
  templateUrl: './document-verification.component.html',
  styleUrls: ['./document-verification.component.scss'],
  standalone: false,
})
export class DocumentVerificationComponent implements OnInit {
  @ViewChild(PrModalConfirmComponent, { static: true })
  modal: PrModalConfirmComponent;

  @Output() onConfirm = new EventEmitter<any>();

  pledge: PledgeRead;
  operation: string = 'SUBSCRIPTION';
  toolbarButtons = DOCUMENT_EDITOR_TOOLBAR_BUTTONS;

  optionInfo1: InfoOption = {
    icon: 'warning',
    backgroundColor: '#FFF3E6',
    iconColor: '#f9a11d',
    textColor: '#f9a11d',
  };
  optionInfo2: InfoOption = {
    icon: 'check',
    backgroundColor: '#E6F9F0',
    iconColor: '#00b67a',
    textColor: '#00b67a',
  };
  optionInfo3: InfoOption = {
    icon: 'info',
  };

  loading = false;
  loadingMessage = '';
  progress = 0;
  private readonly steps = 6;

  ODEALIM_ORGANIZATION_IDS = [
    1, 251251251, 56152307, 85871670, 81880473, 1286361108,
  ];

  sequencageDisabled = false;
  needSequencage = false;
  needSequencageMessage = '';
  isCristalisOrganization: boolean = false;
  cristalisOrganizationId: number = 251251251;
  pledgeOrganizationId: number = null;

  swisslifeMarcheCible: string[] = [
    'Connaissance/expérience financière « approfondie » du souscripteur',
    'Patrimoine net d’emprunt du foyer fiscal supérieur à 750k€',
    'Montant minimal de souscription de 25k€',
  ];

  swisslifePledgeRuleErrors: string[] = [];
  pledgeLcbFtErrors: string = null;
  preVerificationApiError: string = null;
  preVerificationUniversignError: string = null;
  internalError: string = null;
  warningsFields: string[] = [];

  showProcessStep = false;
  processConfirmed = false;
  productProcesses: { productName: string; process: string }[] = [];
  openProcessDropdowns: boolean[] = [];
  processForm!: FormGroup;

  editableDocs: {
    type: 'reglementaire' | 'souscription';
    index: number;
    doc: any;
    title: string;
  }[] = [];
  form!: FormGroup;
  openDropdowns: boolean[] = [];
  validatedDocs: boolean[] = [];


  constructor(
    private subscriptionFacade: SubscriptionFacadeService,
    private sanitizer: DomSanitizer,
    private attributionDao: AttributionDao,
    private pledgeDao: PledgeDao,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.pledge = this.subscriptionFacade.getPledge();
    this.operation = this.pledge.operation || 'SUBSCRIPTION';
    this.form = this.fb.group({ docs: this.fb.array([]) });
    this.processForm = this.fb.group({ processes: this.fb.array([]) });

    combineLatest([
      this.attributionDao.getOrganization(),
      this.pledgeDao.checkNeedSequencage(this.pledge.id, this.pledge),
    ]).subscribe(([organization, responseSequencage]) => {
      this.pledgeOrganizationId = organization.id;
      this.isCristalisOrganization =
        this.cristalisOrganizationId === organization.id;
      this.needSequencage = responseSequencage?.needSequencage || false;
      this.sequencageDisabled = responseSequencage?.sequencageDisabled || false;
      this.needSequencageMessage = responseSequencage?.message || '';
    });

    if (
      !this.pledge.parameters?.processRead &&
      !!this.pledgeOrganizationId &&
      this.ODEALIM_ORGANIZATION_IDS.includes(this.pledgeOrganizationId)
    ) {
      this.productProcesses = (this.pledge.products || [])
        .filter(p => p.parameters?.process)
        .map(p => ({
          productName: p.productName,
          process: p.parameters.process,
        }));

      if (this.productProcesses.length > 0) {
        this.productProcesses.forEach(p =>
          this.processesArray.push(new FormControl(p.process)),
        );
        this.openProcessDropdowns = this.productProcesses.map(
          (_, i) => i === 0,
        );
        this.showProcessStep = true;
        return;
      }
    }

    const { progress } = this.pledge;
    if (['OPEN', 'DRAFT'].includes(progress)) {
      this.regenerateDocuments();
    }
  }

  confirmProcessRead(): void {
    if (!this.processConfirmed) return;

    this.loading = true;
    this.loadingMessage = 'Enregistrement de la lecture du process…';

    this.subscriptionFacade
      .putPledge({
        ...this.pledge,
        parameters: {
          ...this.pledge.parameters,
          processRead: true,
        },
      })
      .subscribe({
        next: (result: PledgeRead) => {
          this.pledge = result;
          this.showProcessStep = false;
          const { progress } = this.pledge;
          if (['OPEN', 'DRAFT'].includes(progress)) {
            this.regenerateDocuments();
          } else {
            this.loading = false;
          }
        },
        error: () => {
          this.internalError = 'Erreur lors de la sauvegarde.';
          this.loading = false;
        },
      });
  }

  regeneratePackReglementaire() {
    this.loading = true;
    this.progress = 0;
    this.subscriptionFacade.setShowSidebarStepper(false);
    this.loadingMessage = 'Génération du pack réglementaire…';
    this.subscriptionFacade
      .putPledge({
        ...this.pledge,
        parameters: {
          ...this.pledge.parameters,
          forceRegeneratePackReglementaire: true,
        },
        commands: [CommandEnum.GENERATE_PACK_REGLEMENTAIRE],
      })
      .pipe(
        tap(() => this.updateProgress('Génération du pack réglementaire…')),
        switchMap((result: PledgeRead) => {
          this.pledge = result;
          if (
            result?.parameters?.packReglementaireHtml?.length ||
            result?.parameters?.packSouscriptionHtml?.length
          ) {
            this.enableHtmlEditing();
          }

          return this.pledgeDao.checkNeedSequencage(result.id, result).pipe(
            tap(responseSequencage => {
              this.needSequencage = responseSequencage?.needSequencage || false;
              this.sequencageDisabled =
                responseSequencage?.sequencageDisabled || false;
              this.needSequencageMessage = responseSequencage?.message || '';
            }),
            switchMap(() => [result]),
          );
        }),
        catchError((err: any) => {
          this.internalError =
            'Une erreur interne est survenue. Veuillez contacter le support technique.';
          return throwError(err);
        }),
        finalize(() => this.resetLoader()),
      )
      .subscribe((result: PledgeRead) => {});
  }

  get docsArray(): FormArray {
    return this.form.get('docs') as FormArray;
  }

  get processesArray(): FormArray {
    return this.processForm.get('processes') as FormArray;
  }

  private loadEditableDocs() {
    this.editableDocs = [];

    const reglementaires = this.pledge.parameters?.packReglementaireHtml || [];
    const souscriptions = this.pledge.parameters?.packSouscriptionHtml || [];

    reglementaires.forEach((list, i) => {
      list.forEach(doc =>
        this.editableDocs.push({
          type: 'reglementaire',
          index: i,
          doc: { ...doc },
          title: doc.title?.toUpperCase(),
        }),
      );
    });

    souscriptions.forEach((list, i) => {
      list.forEach(doc =>
        this.editableDocs.push({
          type: 'souscription',
          index: i,
          doc: { ...doc },
          title: doc.title?.toUpperCase(),
        }),
      );
    });

    this.openDropdowns = new Array(this.editableDocs.length).fill(false);
    this.validatedDocs = new Array(this.editableDocs.length).fill(false);
  }

  toggleAllValidations() {
    const allChecked = this.allDocsValidated();
    this.validatedDocs = this.validatedDocs.map(() => !allChecked);
  }

  enableHtmlEditing() {
    this.loadEditableDocs();
    this.docsArray.clear();
    this.editableDocs.forEach(d =>
      this.docsArray.push(new FormControl(d.doc.html)),
    );
  }

  toggleDropdown(i: number) {
    this.openDropdowns[i] = !this.openDropdowns[i];
  }

  toggleProcessDropdown(i: number) {
    this.openProcessDropdowns[i] = !this.openProcessDropdowns[i];
  }

  toggleValidation(i: number) {
    this.validatedDocs[i] = !this.validatedDocs[i];
  }

  allDocsValidated(): boolean {
    return this.validatedDocs.length > 0 && this.validatedDocs.every(v => v);
  }

  saveEditedDocuments() {
    if (!this.allDocsValidated()) return;

    const updatedHtmls = this.docsArray.value;

    const newReglementaire: any[] = [];
    const newSouscription: any[] = [];
    let idx = 0;

    this.editableDocs.forEach(item => {
      const updatedDoc = { ...item.doc, html: updatedHtmls[idx++] };
      if (item.type === 'reglementaire') {
        if (!newReglementaire[item.index]) newReglementaire[item.index] = [];
        newReglementaire[item.index].push(updatedDoc);
      } else {
        if (!newSouscription[item.index]) newSouscription[item.index] = [];
        newSouscription[item.index].push(updatedDoc);
      }
    });

    const updatedPledge = {
      ...this.pledge,
      parameters: {
        ...this.pledge.parameters,
        packReglementaireHtml: newReglementaire,
        packSouscriptionHtml: newSouscription,
      },
    };

    this.loading = true;
    this.loadingMessage = 'Sauvegarde des documents modifiés…';

    this.subscriptionFacade
      .putPledge(updatedPledge)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: result => {
          this.pledge = result;
          this.subscriptionFacade.nextStep();
        },
        error: err => {
          console.error('Erreur lors de la sauvegarde :', err);
          this.internalError = 'Erreur lors de la sauvegarde des documents.';
        },
      });
  }

  regenerateDocuments() {
    this.swisslifePledgeRuleErrors = [];
    this.pledgeLcbFtErrors = null;
    this.internalError = null;
    this.preVerificationApiError = null;
    this.preVerificationUniversignError = null;
    this.subscriptionFacade.setShowSidebarStepper(false);
    this.loading = true;
    this.progress = 0;
    this.loadingMessage = 'Sauvegarde des données…';

    const swisslifeCustomRulesErrors = verifySwisslifeCustomPledgeRules(
      this.pledge,
    );
    if (swisslifeCustomRulesErrors.length > 0) {
      this.swisslifePledgeRuleErrors = swisslifeCustomRulesErrors;
      this.resetLoader();
      return;
    }

    this.subscriptionFacade
      .putPledge({
        ...this.pledge,
        commands: [CommandEnum.SAVE_OWNERS_DATA],
      })
      .pipe(
        catchError((err: any) => {
          this.internalError = 'Erreur lors de la sauvegarde des données.';
          return throwError(err);
        }),
        tap(() => this.updateProgress('Complétion des données…')),
        switchMap(p0 =>
          this.subscriptionFacade.putPledge({
            ...p0,
            commands: [CommandEnum.FILL_OPERATION_PLEDGE_PRODUCT],
          }),
        ),
        tap(() => this.updateProgress('Vérification de la conformité…')),
        switchMap(p1 =>
          this.subscriptionFacade.putPledge({
            ...p1,
            commands: [
              CommandEnum.VERIFICATION_PLEDGE_RULES,
              CommandEnum.VERIFICATION_PLEDGE_PARAMETERS,
              // CommandEnum.VERIFICATION_CONFORMITY,
            ],
          }),
        ),
        catchError((err: any) => {
          if (
            err?.error?.message === 'Pledge rules not validated' &&
            Array.isArray(err?.error?.errors)
          ) {
            this.swisslifePledgeRuleErrors = err.error.errors;
            return throwError(err);
          }

          if (
            err?.error?.developperMessage?.includes(
              'VerificationConformityException',
            )
          ) {
            this.pledgeLcbFtErrors = err.error.message;
            return throwError(err);
          }

          this.internalError =
            'Une erreur interne est survenue. Veuillez contacter le support technique.';
          return throwError(err);
        }),
        tap(() => this.updateProgress('Calcul des commissions…')),
        switchMap(p2 =>
          this.subscriptionFacade.putPledge({
            ...p2,
            commands: this.isCristalisOrganization
              ? []
              : [CommandEnum.COMPUTE_COMMISSION],
          }),
        ),
        tap(() =>
          this.updateProgress('Génération du pack souscription/opération…'),
        ),
        switchMap(p3 =>
          this.subscriptionFacade.putPledge({
            ...p3,
            commands: [
              CommandEnum.PROCESS_UNIVERSIGN_PREVALIDATION_COMMAND_CONSUMER,
              CommandEnum.GENERATE_SUBSCRIPTION_FILE,
            ],
          }),
        ),
        catchError((err: any) => {
          const { error = {} } = err as any;

          if (
            err?.error?.developperMessage?.includes(
              'PreVerificationExternalApiException',
            )
          ) {
            this.preVerificationUniversignError = err.error.message;
            return throwError(err);
          }

          if (error?.developperMessage?.includes('maximum 4Mo')) {
            this.preVerificationUniversignError =
              'Les pieces d’identité (Passeport, CNI…) de plus de 4Mo sont rejetées par universign merci de les remplacer.';
            return throwError(err);
          }

          if (
            error?.developperMessage?.includes('Universign') &&
            error?.developperMessage?.includes('Email is invalid')
          ) {
            this.preVerificationUniversignError =
              "L'email renseigné pour la signature électronique est invalide. Merci de le corriger dans les informations souscripteur.";
            return throwError(err);
          }

          if (error?.developperMessage?.includes('upload_maximum_size')) {
            this.preVerificationUniversignError =
              'Les pieces d’identité (Passeport, CNI…) de plus de 4Mo sont rejetées par universign merci de les remplacer.';
            return throwError(err);
          }

          if (error?.developperMessage?.includes('Too many files')) {
            this.preVerificationUniversignError =
              "Vous devez télécharger soit un seul document (passeport), soit deux documents (recto et verso de la carte d'identité). Merci de corriger les fichiers téléchargés.";
            return throwError(err);
          }

          this.internalError =
            'Une erreur interne est survenue. Veuillez contacter le support technique.';
          return throwError(err);
        }),
        tap(() => this.updateProgress('Génération du pack réglementaire…')),
        switchMap(p4 =>
          this.subscriptionFacade.putPledge({
            ...p4,
            commands: [CommandEnum.GENERATE_PACK_REGLEMENTAIRE],
          }),
        ),
        // Séquençage V2 — Pour les pledges V2, on enchaîne après le pack réglementaire la
        // génération des packs Adéquation/BS + le HTML éditable Froala des deux packs (régl +
        // adéquation). Split en 4 putPledge successifs (1 commande par appel) pour éviter les
        // timeouts en prod sur le batch — alignement sur le pattern legacy au-dessus
        // (SAVE_OWNERS_DATA → FILL → VERIFICATION → COMPUTE_COMMISSION → GENERATE_PACK_REGLEMENTAIRE).
        // Skip pour les pledges legacy (les commandes V2 sont no-op côté backend mais on évite
        // les aller-retours réseau).
        switchMap(p4bis => {
          if (p4bis?.legacyFlow !== false) {
            return of(p4bis);
          }
          this.updateProgress('Génération du pack adéquation…');
          return this.subscriptionFacade.putPledge({
            ...p4bis,
            commands: [CommandEnum.GENERATE_PACK_ADEQUATION],
          });
        }),
        switchMap(p4ter => {
          if (p4ter?.legacyFlow !== false) {
            return of(p4ter);
          }
          this.updateProgress(
            'Génération du pack bulletins de souscription…',
          );
          return this.subscriptionFacade.putPledge({
            ...p4ter,
            commands: [CommandEnum.GENERATE_PACK_BS],
          });
        }),
        switchMap(p4quater => {
          if (p4quater?.legacyFlow !== false) {
            return of(p4quater);
          }
          this.updateProgress(
            'Préparation des documents éditables du pack réglementaire…',
          );
          return this.subscriptionFacade.putPledge({
            ...p4quater,
            commands: [CommandEnum.GENERATE_PACK_REGLEMENTAIRE_HTML],
          });
        }),
        switchMap(p4quinquies => {
          if (p4quinquies?.legacyFlow !== false) {
            return of(p4quinquies);
          }
          this.updateProgress(
            'Préparation des documents éditables du pack adéquation…',
          );
          return this.subscriptionFacade.putPledge({
            ...p4quinquies,
            commands: [CommandEnum.GENERATE_PACK_ADEQUATION_HTML],
          });
        }),
        tap(() => this.updateProgress('Pré-vérification API externe…')),
        switchMap(p5 => {
          // Séquençage V2 — en V2 (legacyFlow === false), la pré-vérification API externe
          // n'est jouée que sur un périmètre FULL (scope LEGAL_ONLY : pas d'appel API externe
          // à pré-valider). En legacy, comportement inchangé : pas de filtre sur le scope.
          const scopeBlocksV2 = p5?.legacyFlow === false && p5?.scope !== 'FULL';
          if (
            !['SUBSCRIPTION', 'VERSEMENT_COMPLEMENTAIRE'].includes(
              this.operation,
            ) ||
            scopeBlocksV2
          ) {
            return of(p5);
          }
          return this.subscriptionFacade
            .putPledge({
              ...p5,
              commands: [CommandEnum.PRE_VERIFICATION_EXTERNAL_API],
            })
            .pipe(
              catchError((err: any) => {
                if (
                  err?.error?.developperMessage?.includes(
                    'PreVerificationExternalApiException',
                  )
                ) {
                  this.preVerificationApiError = err.error.message;
                }
                // Toujours re-propager : un catchError qui renvoie undefined fait
                // lever à RxJS "You provided 'undefined' where a stream was expected"
                // et masque l'erreur d'origine (on retombait sur l'internalError générique).
                return throwError(() => err);
              }),
            );
        }),
        catchError(err => {
          this.internalError =
            'Une erreur interne est survenue. Veuillez contacter le support technique.';
          this.resetLoader();
          throw err;
        }),
        finalize(() => this.resetLoader()),
      )
      .subscribe((result: PledgeRead) => {
        this.pledge = result;

        if (
          result?.parameters?.packReglementaireHtml?.length ||
          result?.parameters?.packSouscriptionHtml?.length
        ) {
          this.enableHtmlEditing();
        }

        if (result?.warningsLcbFt?.length) {
          this.warningsFields = result.warningsLcbFt;
          this.modal.open();
        }
      });
  }

  private updateProgress(newMessage: string) {
    this.progress += 100 / this.steps;
    this.loadingMessage = newMessage;
  }

  private resetLoader() {
    this.loading = false;
    this.loadingMessage = '';
    this.progress = 0;
    this.subscriptionFacade.loaded();
    this.subscriptionFacade.setShowSidebarStepper(true);
  }

  prev() {
    this.subscriptionFacade.previousStep();
  }

  /**
   * Séquençage V2 — Navigation depuis l'écran de construction des packs V2
   * (PackBuilderComponent — couvre Réglementaire + Adéquation + BS). Le composant V2 a déjà
   * fire la cascade de génération et géré la régénération forcée si applicable.
   */
  onV2Next(updatedPledge: any) {
    if (updatedPledge) {
      this.pledge = updatedPledge;
    }
    this.subscriptionFacade.nextStep();
  }

  onV2Prev() {
    this.subscriptionFacade.previousStep();
  }

  closeModalLcbFt() {
    this.modal.close();
  }
}
